/* The record page (ADR-0035 step three). Tabs over panels; nothing here is a colour literal —
   ADR-0007's rule, enforced by stylelint rather than by review. */

.header {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.chip {
  padding: 2px 8px;
  border: 1px solid var(--line-2);
  border-radius: 999px;
  background: var(--panel-2);
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--ink-2);
  white-space: nowrap;
}

.chipOk {
  border-color: var(--accent-line);
  background: var(--accent-w);
  color: var(--accent);
}

/* Superseded is a **warning**, not a criticality: an earlier revision is a legitimate historical
   fact, and colouring it like a failure would tell a reader somebody did something wrong. */
.chipWarn {
  border-color: var(--warn-line);
  background: var(--warn-w);
  color: var(--warn);
}

.restated {
  margin: 0 0 10px;
  padding: 8px 10px;
  border: 1px solid var(--warn-line);
  border-left-width: 3px;
  border-radius: 7px;
  background: var(--warn-w);
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

/* Tabs, not toggles: unlike the register's scope cards there is no "all tabs" state to return to,
   so `aria-current` is the right signal rather than `aria-pressed`. */
.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  border-bottom: 1px solid var(--line);
}

.tab {
  padding: 7px 12px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: none;
  font: inherit;
  font-size: var(--t-dense);
  color: var(--ink-2);
  cursor: pointer;
}

.tab:hover {
  color: var(--ink);
}

/* The second level of tabs (a record's, the figures' and the sources') on a phone: a finger's height. */
@media (max-width: 560px) {
  .tab {
    min-height: var(--target-touch);
  }
}

.tabOn {
  border-bottom-color: var(--accent);
  color: var(--accent);
  font-weight: 650;
}

/* ------------------------------------------------------------------------------ facts */

.kv {
  display: grid;
  grid-template-columns: 116px 1fr;
  gap: 5px 12px;
  margin: 0;
  font-size: var(--t-min);
}

.k {
  color: var(--muted);
}

.v {
  margin: 0;
  color: var(--ink);
  overflow-wrap: anywhere;
}

.mono {
  font-family: var(--mono);
  font-size: var(--t-min);
  color: var(--ink-2);
}

/* ------------------------------------------------------------------------------ figures */

.figures {
  list-style: none;
  margin: 0;
  padding: 0;
}

.figureRow {
  display: grid;
  grid-template-columns: 1fr auto 12px;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 0;
  border: 0;
  border-bottom: 1px solid var(--line);
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.figureRow:hover {
  background: var(--panel-2);
}

.figureName {
  display: flex;
  flex-direction: column;
  font-size: var(--t-dense);
  color: var(--ink);
}

.figureSub {
  font-size: var(--t-min);
  color: var(--muted);
}

.figureValue {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
}

.chev {
  color: var(--muted);
}

/* ------------------------------------------------------------------------ a document's figures */

.docFigures {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}

.docFigure {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--line);
  font-size: var(--t-dense);
}

.docFigure:last-child {
  border-bottom: 0;
}

.docFigureMeta {
  color: var(--muted);
  font-size: var(--t-min);
}

.docFigureTonnes {
  margin-left: auto;
  color: var(--ink);
  font-weight: 600;
  font-variant-numeric: lining-nums tabular-nums;
}

/* ------------------------------------------------------------------------------ workflow */

.stages {
  list-style: none;
  margin: 0 0 10px;
  padding: 0;
}

.stage {
  display: flex;
  flex-direction: column;
  padding: 6px 0 6px 10px;
  border-left: 2px solid var(--line-2);
}

.stageHere {
  border-left-color: var(--accent);
}

.stageDone {
  border-left-color: var(--accent-line);
}

.stageName {
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
}

.stageWho {
  font-size: var(--t-min);
  color: var(--muted);
}

.form {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
}

.actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0 0;
}

.steps {
  margin: 0 0 10px;
  padding-left: 18px;
  font-size: var(--t-min);
  line-height: 1.6;
  color: var(--ink-2);
}

.steps li + li {
  margin-top: 8px;
}

/* ------------------------------------------------------------------------ lists of work */

.list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.item {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--line);
}

.itemMain {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.itemTitle {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--t-dense);
  font-weight: 600;
  color: var(--ink);
}

.itemBody {
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
  overflow-wrap: anywhere;
}

.itemMeta {
  font-size: var(--t-min);
  color: var(--muted);
}

/* Three severities, three appearances. Flattening them would make the scale one value, which is
   the point of having it. */
.sev {
  display: inline-block;
  padding: 1px 6px;
  border: 1px solid var(--line-2);
  border-radius: 999px;
  background: var(--panel-2);
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--muted);
}

.sev::first-letter {
  text-transform: uppercase;
}

.sevWarning {
  border-color: var(--warn-line);
  background: var(--warn-w);
  color: var(--warn);
}

.sevBlocking {
  border-color: var(--crit-line);
  background: var(--crit-w);
  color: var(--crit);
}

.sevDone {
  border-color: var(--accent-line);
  background: var(--accent-w);
  color: var(--accent);
}

/* ------------------------------------------------------------------------- revision chain */

.chain {
  list-style: none;
  margin: 0;
  padding: 0;
}

.node {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 0 10px 12px;
  border-left: 2px solid var(--line-2);
}

.nodeHere {
  border-left-color: var(--accent);
  background: var(--panel-2);
}

.nodeHead {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--t-dense);
  font-weight: 650;
  color: var(--ink);
}

.nodeBody {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
}

.nodeMeta {
  font-size: var(--t-min);
  color: var(--muted);
}

/* Accepting a figure that has no document (card C-E). The refusal is a warning, not a failure: a
   rule applied to this figure and this person is the product working. The head carries the meaning
   in words as well as colour (design rule 8). */
.verifyRefusal {
  margin: 10px 0 0;
  padding: 8px 10px;
  border: 1px solid var(--warn-line);
  border-left-width: 3px;
  border-radius: 7px;
  background: var(--warn-w);
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

.verifyOk {
  margin: 0;
  padding: 8px 10px;
  border: 1px solid var(--accent-line);
  border-left-width: 3px;
  border-radius: 7px;
  background: var(--accent-w);
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

.verifyHead {
  display: block;
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 2px;
}

.verifyFlags {
  display: block;
  margin: 6px 0 0;
  padding-left: 18px;
}

/* The record page's tab panel, as the twelve-column grid its panels are placed on.
   `RecordPanel` renders one `div`; without this it is a single grid item of the shell's body, one
   column wide, and every panel inside it (`column="1 / 7"`) was squeezed into about 87px at 1440 -
   found by the journey walk of 2026-10-31, on all five tabs of every record. It takes the row the
   header panel leaves, and scrolls inside itself like the screens that stack their tab. */
.tabGrid {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: var(--gap);
  align-content: start;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
}

@media (max-width: 900px) {
  .tabGrid {
    grid-template-columns: minmax(0, 1fr);
    overflow-y: visible;
  }
}

/* The sign-off card spans the Approvals tab's grid, above the panels. */
.signoff {
  grid-column: 1 / -1;
  min-width: 0;
}
